import { DocumentSmartTextDateFormat, DocumentSmartTextType } from "./types";

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})/;
const ISO_DATETIME_TIME_RE = /T(\d{2}):(\d{2})/;

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];

const LONG_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];

const SHORT_WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const LONG_WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday"
];

const applyDatePattern = (
  yyyy: string,
  mm: string,
  dd: string,
  pattern: DocumentSmartTextDateFormat
): string => {
  const monthIndex = Number(mm) - 1;
  const day = Number(dd);
  const year = Number(yyyy);
  const date = new Date(Date.UTC(year, monthIndex, day));
  const shortMonth = SHORT_MONTHS[monthIndex] ?? mm;
  const longMonth = LONG_MONTHS[monthIndex] ?? mm;
  const shortWeekday = SHORT_WEEKDAYS[date.getUTCDay()] ?? "";
  const longWeekday = LONG_WEEKDAYS[date.getUTCDay()] ?? "";

  switch (pattern) {
    case "MM/DD/YYYY":
      return `${mm}/${dd}/${yyyy}`;
    case "MMM D":
      return `${shortMonth} ${day}`;
    case "D MMMM YYYY":
      return `${day} ${longMonth} ${yyyy}`;
    case "DDD, D MMM YYYY":
      return `${shortWeekday}, ${day} ${shortMonth} ${yyyy}`;
    case "DDDD DD/MM/YYYY":
      return `${longWeekday} ${dd}/${mm}/${yyyy}`;
    case "DDDD, D MMMM YYYY":
      return `${longWeekday}, ${day} ${longMonth} ${yyyy}`;
    case "DD/MM/YYYY":
    default:
      return `${dd}/${mm}/${yyyy}`;
  }
};

/**
 * Formats an ISO date / datetime-local value using the supplied pattern.
 *
 * - `date` type values look like `YYYY-MM-DD` (from `<input type="date">`).
 * - `datetime` type values look like `YYYY-MM-DDTHH:mm` (from
 *   `<input type="datetime-local">`); the date portion is reformatted and the
 *   `HH:mm` time portion is appended unchanged.
 *
 * Returns the raw input unchanged when:
 *   - the value is empty / not a string,
 *   - the type isn't `date` / `datetime`,
 *   - the value doesn't match the expected ISO shape (defensive fallback so
 *     half-typed or unexpected values never break textbox rendering).
 *
 * When `pattern` is undefined for a date/datetime, defaults to `DD/MM/YYYY` —
 * keeps legacy fields (stored before `format` was persisted) rendering
 * consistently with newly-created ones, which now write the default on
 * creation via `addDocumentSmartTextField`.
 */
export const formatDocumentDateValue = (
  rawValue: string | null | undefined,
  type: DocumentSmartTextType,
  pattern: DocumentSmartTextDateFormat | undefined
): string => {
  if (!rawValue) return rawValue ?? "";
  if (type !== "date" && type !== "datetime") return rawValue;
  const effectivePattern: DocumentSmartTextDateFormat = pattern ?? "DD/MM/YYYY";

  const dateMatch = rawValue.match(ISO_DATE_RE);
  if (!dateMatch) return rawValue;
  const yyyy = dateMatch[1];
  const mm = dateMatch[2];
  const dd = dateMatch[3];
  if (!yyyy || !mm || !dd) return rawValue;
  const formattedDate = applyDatePattern(yyyy, mm, dd, effectivePattern);

  if (type !== "datetime") return formattedDate;

  const timeMatch = rawValue.match(ISO_DATETIME_TIME_RE);
  if (!timeMatch) return formattedDate;
  const hh = timeMatch[1];
  const min = timeMatch[2];
  if (!hh || !min) return formattedDate;
  return `${formattedDate} ${hh}:${min}`;
};

// Day 25 / month 12 disambiguates day-vs-month ordering; Thursday gives a
// meaningful weekday for the label-based formats.
const PLACEHOLDER_SAMPLE_DATE = "2025-12-25";
const PLACEHOLDER_SAMPLE_DATETIME = "2025-12-25T09:30";

/**
 * Builds the empty-field overlay hint by rendering a sample date in the field's
 * selected format, e.g. `DD/MM/YYYY` → `25/12/2025`, `MMM D` → `Dec 25`. Shown
 * directly to the user, so it must read as an example date rather than the
 * lowercased pattern tokens.
 */
export const getDocumentDatePlaceholder = (
  pattern: DocumentSmartTextDateFormat | undefined,
  type: DocumentSmartTextType
): string => {
  const sample =
    type === "datetime"
      ? PLACEHOLDER_SAMPLE_DATETIME
      : PLACEHOLDER_SAMPLE_DATE;
  return formatDocumentDateValue(sample, type, pattern);
};
